<script lang="ts">
import type { InputTimeProps } from '@nuxt/ui';
import type { PrimitiveType } from '#layers/data-model';
import { PRIMITIVE_TYPE_TIME } from '#layers/data-model';

export const supportedTimeFieldTypes = ({ primitiveType }: { primitiveType: PrimitiveType }) =>
	primitiveType.name === PRIMITIVE_TYPE_TIME ? ['datetime'] : [];

const __DsBlock = {
	type: 'input',
	icon: 'lucide:clock',
	name: '$t:blocks.input_time.name',
	description: '$t:blocks.input_time.description',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: {
		supportedTypes: supportedTimeFieldTypes,
		recommendedDisplays: ['$builtin:DisplayFormattedTime'],
	},

	options: [
		{
			name: 'modelValue',
			model: true,
		},
		{
			name: 'color',
			type: 'enum',
			values: BLOCK_COLOR_OPTIONS,
		},
		{
			name: 'variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'disabled',
		},
		{
			name: 'readonly',
		},
		{
			name: 'disabledNotice',
		},
	],
}</script>

<script lang="ts" setup>
import type { Temporal } from 'temporal-polyfill';
import type { InputBlockProps } from '~/types/input-block';
import { reactivePick } from '@vueuse/core';
import { computed } from 'vue';
import { timeConverter } from '#layers/base';
import { defineBlock } from '#layers/block-engine';
import { BLOCK_COLOR_OPTIONS, BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { useDisabledNotice } from '#layers/ui';
import { usePrimitiveFormField } from '~/composables/form-block/use-primitive-form-field';

interface Props extends InputBlockProps, Pick<InputTimeProps, 'color' | 'variant' | 'size' | 'disabled'> {
	block?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	block: true,
});

const forwarded = reactivePick(props, 'color', 'variant', 'size', 'disabled', 'readonly', 'block');

const model = defineModel<Temporal.PlainTime | null>();

const { modelValue: m, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

;

const timeModel = computed({
	get: () => timeConverter.toCalendar(m.value),
	set: (value) => {
		m.value = timeConverter.toTemporal(value);
	},
});
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DInputTime
			v-bind="forwarded"
			v-model="timeModel"
			:aria-readonly="readonly"
		/>
	</DTooltip>
</template>
